<script setup lang="ts">
import { useChapterHeadings } from "../composables/useChapterHeadings";
/**
 * Project search palette. Pure transitions in `ProjectSearchDialog.update.ts`;
 * this shell owns debounce timer, file reads, and jump via `useTea`.
 */
import {
  fromMaybe,
  isJust,
  just,
  nothing,
  toDomAttr,
  type Maybe,
  unwrapMaybeOr,
} from "../lib/maybe";
import { computed, nextTick, useId, useTemplateRef, watch } from "vue";
import { storeToRefs } from "pinia";
import {
  collectSearchHits,
  type SearchHit,
  type SearchSource,
} from "../editor/projectSearch";
import { displayFileName } from "../lib/fileNames";
import { isMapFileName } from "../lib/mapDocument";
import { isDemoRoot } from "../lib/platform";
import { isTauri, joinProjectPath } from "../lib/runtime";
import { createTimeoutHandle } from "../lib/timeoutHandle";
import { readProjectFile } from "../lib/tauri/core";
import {
  chapterFolderFromScenePath,
  PROJECT_DATABASE_DIR,
  PROJECT_NOTES_DIR,
  sceneFilesInOrder,
} from "../lib/zwConfig";
import { assertNever } from "../lib/assertNever";
import { useTea } from "../lib/tea";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useOutlineEntitiesStore } from "../stores/useOutlineEntitiesStore";
import { useProjectWorkspaceStore } from "../stores/useProjectWorkspaceStore";
import ZwDialog from "./ui/ZwDialog.vue";
import ZwInput from "./ui/ZwInput.vue";
import {
  initialSearchModel,
  updateProjectSearchFn,
  type SearchCmd,
} from "./ProjectSearchDialog.update";

const { chapterLabel } = useChapterHeadings();
const props = defineProps<{
  open: boolean;
}>();

const emit = defineEmits<{
  close: [];
}>();

const session = useEditorSessionStore();
const workspace = useProjectWorkspaceStore();
const entities = useOutlineEntitiesStore();
const {
  rootPath,
  outlineActiveFile,
  activeNote,
  activeDatabase,
  doc,
  modLabel,
} = storeToRefs(session);

const listId = useId();
const searchInput = useTemplateRef<InstanceType<typeof ZwInput>>("searchInput");
const listEl = useTemplateRef<HTMLElement>("listEl");

const searchDebounce = createTimeoutHandle();

const { model, dispatch } = useTea(initialSearchModel, updateProjectSearchFn, {
  runCmd: just(runSearchCmd),
});

async function runSearchCmd(cmd: SearchCmd): Promise<void> {
  switch (cmd.kind) {
    case "focusInput":
      await nextTick();
      searchInput.value?.focus();
      searchInput.value?.select();
      return;
    case "clearDebounce":
      searchDebounce.clear();
      return;
    case "scheduleSearch":
      searchDebounce.schedule(220, () => {
        void dispatch({ type: "DebounceElapsed", query: cmd.query });
      });
      return;
    case "runSearch":
      await runProjectSearch(cmd.query);
      return;
    case "scrollActiveIntoView":
      await nextTick();
      {
        const root = listEl.value;
        if (root === null) return;
        const row = root.querySelector<HTMLElement>(
          `[data-search-index="${cmd.index}"]`,
        );
        row?.scrollIntoView({ block: "nearest" });
      }
      return;
    case "jumpToHit":
      void workspace.onSearchJump(cmd.hit.source, cmd.hit.file, cmd.hit.from);
      if (session.mobileShell) session.outlineOpen = false;
      return;
    case "close":
      emit("close");
      return;
    default:
      assertNever(cmd);
  }
}

function chapterDisplayName(path: string): string {
  return chapterLabel(unwrapMaybeOr(chapterFolderFromScenePath(path), path));
}

function sourceLabel(source: SearchSource): string {
  if (source === "note") return "Notes";
  if (source === "database") return "Worldbuilding";
  return "Writing";
}

function searchHitDisplayName(hit: SearchHit): string {
  return hit.source === "chapter"
    ? chapterDisplayName(hit.file)
    : displayFileName(hit.file);
}

function hitKindLabel(hit: SearchHit): string {
  if (hit.kind === "name") return "Name";
  return `${hit.unit === "paragraph" ? "¶" : "L"}${hit.lineNumber}`;
}

const files = (): string[] => sceneFilesInOrder(session.manuscript);

/**
 * The results listbox only renders for a non-empty settled query with hits (see
 * the template chain), so `aria-controls` must follow it.
 */
const listboxShown = computed(
  () =>
    model.value.query.trim().length > 0 &&
    model.value.busy === false &&
    model.value.hits.length > 0,
);

const shortcutHint = computed(() => `${modLabel.value}⇧P`);

const searchQuery = computed({
  get: (): string => model.value.query,
  set: (query: string): void => {
    void dispatch({ type: "QueryChanged", query });
  },
});

async function runProjectSearch(query: string): Promise<void> {
  const needle = query.trim();
  const root = fromMaybe(rootPath.value);
  const canSearch =
    root !== null && (isTauri() === true || isDemoRoot(root) === true);
  if (needle.length === 0 || canSearch === false) {
    await dispatch({ type: "SearchFinished", query, hits: [] });
    return;
  }

  const liveDoc = doc.value;

  try {
    const items = await Promise.all([
      ...files().map(async (name) => {
        let docText: Maybe<string> = nothing;
        try {
          docText = just(
            isJust(outlineActiveFile.value) &&
              name === outlineActiveFile.value.value
              ? liveDoc
              : await readProjectFile(joinProjectPath(root, name)),
          );
        } catch {
          /* skip unreadable */
        }
        return {
          source: "chapter" as const,
          file: name,
          displayName: chapterDisplayName(name),
          text: docText,
        };
      }),
      ...entities.notes.map(async ({ name }) => {
        let docText: Maybe<string> = nothing;
        try {
          docText = just(
            isJust(activeNote.value) && name === activeNote.value.value
              ? liveDoc
              : await readProjectFile(
                  joinProjectPath(root, `${PROJECT_NOTES_DIR}/${name}`),
                ),
          );
        } catch {
          /* skip */
        }
        return {
          source: "note" as const,
          file: name,
          displayName: displayFileName(name),
          text: docText,
        };
      }),
      ...entities.databaseEntries.map(async (name) => {
        const displayName = displayFileName(name);
        if (isMapFileName(name)) {
          return {
            source: "database" as const,
            file: name,
            displayName,
            text: nothing,
          };
        }
        let docText: Maybe<string> = nothing;
        try {
          docText = just(
            isJust(activeDatabase.value) && name === activeDatabase.value.value
              ? liveDoc
              : await readProjectFile(
                  joinProjectPath(root, `${PROJECT_DATABASE_DIR}/${name}`),
                ),
          );
        } catch {
          /* skip */
        }
        return {
          source: "database" as const,
          file: name,
          displayName,
          text: docText,
        };
      }),
    ]);

    await dispatch({
      type: "SearchFinished",
      query,
      hits: collectSearchHits(items, needle),
    });
  } catch {
    await dispatch({ type: "SearchFinished", query, hits: [] });
  }
}

function close(): void {
  emit("close");
}

function onPanelKeydown(event: KeyboardEvent): void {
  if (event.key === "ArrowDown") {
    event.preventDefault();
    void dispatch({ type: "MoveActive", delta: 1 });
    return;
  }
  if (event.key === "ArrowUp") {
    event.preventDefault();
    void dispatch({ type: "MoveActive", delta: -1 });
    return;
  }
  if (event.key === "Enter") {
    event.preventDefault();
    void dispatch({ type: "ActivateActive" });
  }
}

watch(
  () => props.open,
  (open) => {
    if (open === false) {
      void dispatch({ type: "Closed" });
      return;
    }
    void dispatch({ type: "Opened" });
  },
  { immediate: true },
);
</script>

<template>
  <ZwDialog
    :open="open"
    title="Search project"
    title-hidden
    size="palette"
    placement="top"
    :z-index="120"
    :description="nothing"
    @close="close"
  >
    <div class="zw-project-search" @keydown="onPanelKeydown">
      <div class="zw-project-search-field">
        <ZwInput
          :id="nothing"
          ref="searchInput"
          v-model="searchQuery"
          placeholder="Search names and contents…"
          aria-label="Search project"
          :aria-controls="toDomAttr(listboxShown ? just(listId) : nothing)"
          :label="nothing"
        />
      </div>

      <p v-if="model.query.trim().length === 0" class="zw-project-search-hint">
        Search writing, notes, worldbuilding, and maps by name or contents.
        <span class="zw-project-search-kbd">{{ shortcutHint }}</span>
      </p>
      <p v-else-if="model.busy" class="zw-project-search-hint">Searching…</p>
      <p v-else-if="model.hits.length === 0" class="zw-project-search-hint">
        No matches.
      </p>
      <ul
        v-else
        :id="listId"
        ref="listEl"
        class="zw-project-search-list"
        role="listbox"
        aria-label="Search results"
      >
        <li
          v-for="(hit, index) in model.hits"
          :id="`${listId}-item-${index}`"
          :key="`${hit.kind}-${hit.source}-${hit.file}-${hit.from}-${index}`"
          role="option"
          :aria-selected="index === model.activeIndex"
          :data-search-index="index"
        >
          <button
            type="button"
            class="zw-project-search-row"
            :class="{
              'zw-project-search-row-active': index === model.activeIndex,
            }"
            :title="`${sourceLabel(hit.source)} · ${searchHitDisplayName(hit)} · ${hitKindLabel(hit)}`"
            @click="dispatch({ type: 'ActivateHit', hit })"
            @mouseenter="dispatch({ type: 'SetActiveIndex', index })"
          >
            <span class="zw-project-search-meta">
              {{ sourceLabel(hit.source) }}
              <span aria-hidden="true">·</span>
              {{ searchHitDisplayName(hit) }}
              <span aria-hidden="true">·</span>
              {{ hitKindLabel(hit) }}
            </span>
            <span
              v-if="hit.kind === 'content'"
              class="zw-project-search-snippet"
            >
              {{ hit.snippet }}
            </span>
            <span
              v-else
              class="zw-project-search-snippet zw-project-search-snippet-name"
            >
              Open document
            </span>
          </button>
        </li>
      </ul>
    </div>
  </ZwDialog>
</template>

<style scoped>
.zw-project-search {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  min-height: 0;
  flex: 1;
}

.zw-project-search-field {
  flex-shrink: 0;
}

.zw-project-search-hint {
  margin: 0 0.2rem;
  font-size: 0.85rem;
  line-height: 1.4;
  color: color-mix(in oklch, var(--text-muted) 85%, transparent);
}

.zw-project-search-kbd {
  margin-left: 0.35rem;
  font-size: 0.78rem;
  color: var(--text-muted);
  opacity: 0.85;
}

.zw-project-search-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  overflow: auto;
  min-height: 0;
  flex: 1;
}

.zw-project-search-row {
  appearance: none;
  border: 0;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  width: 100%;
  padding: 0.4rem 0.5rem;
  text-align: left;
  font: inherit;
  cursor: pointer;
  border-radius: 0.3rem;
}

.zw-project-search-row:hover,
.zw-project-search-row-active {
  background: var(--hover-fill);
}

.zw-project-search-meta {
  font-size: 0.78rem;
  color: var(--text-muted);
}

.zw-project-search-snippet {
  font-size: 0.9rem;
  line-height: 1.35;
  color: var(--text);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.zw-project-search-snippet-name {
  color: var(--text-muted);
  font-size: 0.82rem;
}
</style>
